/**
 * useHomeGroupsPageConfig Hook
 *
 * Fetches home groups page configuration using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const HomeGroupsPageConfigDocument = `
  query HomeGroupsPageConfig {
    homeGroupsPageConfig {
      id
      title
      bodyText
      imageUrl
      isActive
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface PageConfig {
  id: string;
  title: string | null;
  bodyText: string | null;
  imageUrl: string | null;
  isActive: boolean;
}

interface UseHomeGroupsPageConfigOptions {
  pause?: boolean;
}

interface UseHomeGroupsPageConfigResult {
  config: PageConfig | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch home groups page configuration.
 */
export function useHomeGroupsPageConfig(
  options: UseHomeGroupsPageConfigOptions = {}
): UseHomeGroupsPageConfigResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{
    homeGroupsPageConfig: PageConfig | null;
  }>({
    query: HomeGroupsPageConfigDocument,
    pause,
  });

  return {
    config: result.data?.homeGroupsPageConfig ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
